<!-- Sidebar.vue -->
<template>
  <aside class="offcanvas offcanvas-start sidebar-offcanvas" tabindex="-1" id="sidebar" aria-labelledby="sidebarLabel"
    data-bs-backdrop="true" data-bs-scroll="false">
    <!-- LOGO / HEADER -->
    <div class="offcanvas-header sidebar-header">
      <div class="w-100 text-center">
        <div class="logo-container mb-3"></div>
      </div>
    </div>
  
    <!-- MENU -->
    <div class="offcanvas-body px-3">
      <nav class="nav flex-column sidebar-nav">
  
        <!-- DASHBOARD: tetap statis, bukan bagian dari modul SSO -->
        <router-link class="nav-link sidebar-link" to="/dashboard">
          <i class="bi bi-speedometer2 me-2"></i>
          Dashboard
        </router-link>
  
        <!-- Loading / error state -->
        <div v-if="loading" class="text-center text-muted small py-3">
          Memuat menu...
        </div>
        <div v-else-if="loadError" class="text-center text-danger small py-3">
          Gagal memuat menu.
        </div>
  
        <!-- MENU DINAMIS DARI SSO (khusus menu MONDOCT) -->
        <template v-for="(module, index) in modules" :key="'module-' + module.module_id">
  
          <!-- Modul TANPA submodule -->
          <router-link v-if="module.list_submodule.length === 0" class="nav-link sidebar-link" :to="module.url || '#'">
            <i class="bi me-2" :class="resolveIcon(module.icon, module.url)"></i>
            {{ module.module_name }}
          </router-link>
  
          <!-- Modul DENGAN submodule -->
          <div v-else>
            <div class="nav-link sidebar-link d-flex justify-content-between align-items-center" href="#"
              @click.prevent="toggleModule(index)">
              <i class="bi me-2" :class="resolveIcon(module.icon, module.url)"></i>
              <span class="flex-grow-1">{{ module.module_name }}</span>
              <i class="bi toggle-icon" :class="openModules[index] ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
  
            <!-- SUBMENU -->
            <transition name="submenu-fade">
              <div v-show="openModules[index]" class="submenu-wrapper">
                <router-link v-for="submodule in module.list_submodule" :key="'submodule-' + submodule.submodule_id"
                  class="nav-link sidebar-link sidebar-sublink" :to="submodule.url || '#'">
                  <i class="bi me-2" :class="resolveIcon(submodule.icon, submodule.url)"></i>
                  {{ submodule.submodule_name }}
                </router-link>
              </div>
            </transition>
          </div>
  
        </template>
  
      </nav>
    </div>
  </aside>
</template>

<script>
import { Offcanvas } from "bootstrap";
import api from "@/services/api";

const MENU_NAME = "MONDOC";

const ICON_FALLBACK_BY_URL = {
  "/monitoring-document": "bi-file-earmark-text",
  "/transport": "bi-file-earmark-text",
  "/transport/transport-offers": "bi-file-earmark-text",
  "/transport/transport-offers-pks": "bi-display",
  "/asset": "bi-pc-display-horizontal",
  "/asset/asset-it": "bi-laptop",
  "/asset/infrastructure-it": "bi-hdd-network",
  "/master": "bi-gear",
  "/report/report-overdue": "bi-file-earmark-text",
  "/report/report-overdue-sph": "bi-file-earmark-text",
  "/report/monitoring-sph-spk": "bi-file-earmark-text",
  "/report/approved-supplier": "bi-file-earmark-text",
  "/report/report-asset-number": "bi-file-earmark-text",
  "/report/report-contract-status": "bi-file-earmark-text",
  "/report/report-expiring": "bi-file-earmark-text",
  "/report/opname-license": "bi-file-earmark-text",
  "/report/inventaris-asset": "bi-file-earmark-text",
  "/report/asset-with-number": "bi-file-earmark-text",
  "/evaluation/evaluation-approval": "bi-file-earmark-text",
};

export default {
  name: "SidebarComponent",

  data() {
    return {
      modules: [],
      openModules: {}, // { [index]: true/false } -> state expand tiap modul
      loading: false,
      loadError: false,
    };
  },

  watch: {
    // Tutup offcanvas sidebar tiap kali pindah route
    $route: {
      immediate: true,
      handler(to) {
        // Auto-buka submodule kalau route aktif ada di path submodule-nya
        this.modules.forEach((module, index) => {
          const isInside = (module.list_submodule || []).some((sub) =>
            sub.url && to.path.startsWith(this.normalizePath(sub.url))
          );
          if (isInside) this.openModules[index] = true;
        });

        this.$nextTick(() => {
          const sidebarEl = document.getElementById("sidebar");
          const instance = Offcanvas.getInstance(sidebarEl);
          if (instance) {
            instance.hide();
          }
        });
      },
    },
  },

  created() {
    this.fetchModules();
  },

  methods: {
    normalizePath(url) {
      if (!url) return url;
      return url.startsWith("/") ? url : "/" + url;
    },

    resolveIcon(icon, url) {
      if (icon && icon !== "-") return icon;

      const key = this.normalizePath(url);
      if (key && ICON_FALLBACK_BY_URL[key]) {
        return ICON_FALLBACK_BY_URL[key];
      }

      return "bi-file-earmark-text";
    },

    toggleModule(index) {
      this.openModules[index] = !this.openModules[index];
    },

    fetchModules() {
      this.loading = true;
      this.loadError = false;

      api
        .get(`/module/by-menu-name/${MENU_NAME}`)
        .then(({ data }) => {
          // Response: { success, data: [ { menu_id, menu_name, modules: [...] } ] }
          const menu = data.data?.[0];
          const rawModules = menu?.modules || [];

          this.modules = rawModules
            .map((module) => ({
              ...module,
              url: this.normalizePath(module.url),
              list_submodule: (module.list_submodule || [])
                .slice()
                .sort((a, b) => a.sequence - b.sequence)
                .map((submodule) => ({
                  ...submodule,
                  url: this.normalizePath(submodule.url),
                })),
            }))
            .sort((a, b) => a.sequence - b.sequence);
        })
        .catch((error) => {
          console.error("Gagal ambil menu dari SSO:", error);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
